import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStore from "../../Store/Store";
import SimulationTutorial from "../TutorialComponents/SimulationTutorial";
import { createPortal } from "react-dom";

/**
 * Overlay component that covers the iframe during simulation generation
 * Shows a foggy backdrop with "Generating simulation on diagram" message
 */
const SimulationGenerationOverlay: React.FC = () => {
  const theme = useTheme();
  const [now, setNow] = useState<number>(Date.now());

  // For simulation generation detection, we'll check if there's active generation happening
  // This would need to be tracked in the store - for now, we'll track a combination of states
  const { selectedSimulationKey, currentUnsavedSimulationObject } = useStore(
    (state) => ({
      selectedSimulationKey: state.simulationsState.selectedSimulationKey,
      currentUnsavedSimulationObject: state.currentUnsavedSimulationObject,
    })
  );

  // Track simulation generation start time
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );

  // Detect when simulation generation starts
  useEffect(() => {
    // Check if we have a selected AI-generated simulation with keywords but no simSteps
    const isAISimulationGenerating =
      selectedSimulationKey &&
      currentUnsavedSimulationObject &&
      currentUnsavedSimulationObject.isAIGenerated &&
      currentUnsavedSimulationObject.keywords &&
      currentUnsavedSimulationObject.simSteps.length === 0;

    if (isAISimulationGenerating && !generationStartTime) {
      setGenerationStartTime(Date.now());
    } else if (!isAISimulationGenerating && generationStartTime) {
      setGenerationStartTime(null);
    }
  }, [
    selectedSimulationKey,
    currentUnsavedSimulationObject,
    generationStartTime,
  ]);

  // Update timer for progress calculation
  useEffect(() => {
    let timer: number;
    if (generationStartTime) {
      timer = window.setInterval(() => setNow(Date.now()), 1000);
    }
    return () => {
      clearInterval(timer);
    };
  }, [generationStartTime]);

  // Don't show if not generating
  if (!generationStartTime) {
    return null;
  }

  // Calculate progress (45 seconds max, cap at 90% until completion)
  const calculateProgress = (): number => {
    if (!generationStartTime) return 0;
    const elapsed = now - generationStartTime;
    return Math.min((elapsed / 45000) * 90, 90); // Cap at 90% until completion
  };

  const progress = calculateProgress();

  const overlay = (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bgcolor="rgba(0, 0, 0, 0.05)" // Almost transparent dark overlay
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      zIndex={10000100} // Consistent with other tutorial overlays
      sx={{
        backdropFilter: "blur(10px)", // Stronger blur for generation overlay
        pointerEvents: "all", // Prevent interaction with iframe
        overflowY: "auto", // Make the overlay scrollable
        overflowX: "hidden",
        height: "100vh", // Ensure full viewport height
      }}
    >
      {/* Header with progress - compact and fixed */}
      <Box
        width="100%"
        display="flex"
        justifyContent="center"
        sx={{
          flexShrink: 0, // Prevent shrinking
          paddingTop: "8px",
          paddingBottom: "8px",
        }}
      >
        <Box
          width="80%"
          maxWidth={450} // Reduced max width
          textAlign="center"
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderRadius: "20px", // Slightly smaller border radius
            padding: "12px 16px", // Much more compact padding
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)", // Smaller shadow
          }}
        >
          <Typography
            variant="subtitle1" // Even smaller than h6
            mb={1} // Minimal margin
            sx={{
              fontWeight: 600,
              color: theme.custom.darkerGrey,
              fontSize: "16px", // Explicit smaller font size
            }}
          >
            Generating simulation on diagram
          </Typography>

          <Typography
            variant="caption" // Very compact text
            mb={1.5} // Minimal margin
            sx={{
              opacity: 0.8,
              fontWeight: "normal",
              color: theme.custom.darkerGrey,
              lineHeight: 1.3,
              fontSize: "12px",
              display: "block",
            }}
          >
            Creating executable simulation steps and placing them on the
            diagram...
          </Typography>

          <Box
            position="relative"
            height={8} // Even smaller progress bar
            borderRadius={2}
            overflow="hidden"
            bgcolor={theme.custom.lightGrey}
            mb={1} // Minimal margin
            mx="auto"
            width="70%"
          >
            <Box
              position="absolute"
              left={0}
              top={0}
              bottom={0}
              width={`${progress}%`}
              bgcolor={theme.custom.pink}
              sx={{
                transition: "width 0.5s ease, box-shadow 0.5s ease",
                ...(progress >= 80
                  ? { boxShadow: `0 0 8px ${theme.custom.pink}` }
                  : {}),
              }}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              fontStyle: "italic",
              color: theme.custom.darkerGrey,
              fontSize: "11px", // Very small text
            }}
          >
            {Math.round(progress)}% complete • This takes 45 seconds on average
          </Typography>
        </Box>
      </Box>

      {/* Tutorial content - takes remaining space */}
      <Box
        width="100%"
        display="flex"
        justifyContent="center"
        sx={{
          flex: "1 1 auto", // Take remaining space
          minHeight: 0, // Allow flex child to shrink
          overflow: "hidden", // Prevent overflow
        }}
      >
        <Box
          width="90%"
          maxWidth="1200px"
          sx={{
            height: "100%", // Use full available height
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SimulationTutorial
            isPopup={false}
            onClose={() => {}}
            isOverlayMode={true}
          />
        </Box>
      </Box>
    </Box>
  );

  return createPortal(overlay, document.body);
};

export default SimulationGenerationOverlay;
